<script lang="ts" setup>
import { onMounted, useTemplateRef } from 'vue';

import { useLongScroll, isTouchableDevice } from '@libs/utils';

interface AppSnapScrollProps {
  direction: 'X' | 'Y';
  scrollSpacer?: boolean;
}
const props = defineProps<AppSnapScrollProps>();

interface AppSnapScrollEmits {
  (eventName: 'long-scroll'): void;
  (eventName: 'init', element: HTMLElement): void;
}
const emit = defineEmits<AppSnapScrollEmits>();

const contentRef = useTemplateRef('contentRef');

const { longScrollEvent } = useLongScroll(contentRef);

longScrollEvent.subscribe(() => emit('long-scroll'));

onMounted(() => {
  if (contentRef.value) {
    emit('init', contentRef.value);
  }
});
</script>

<template>
  <div
    class="app-snap-scroll"
    :class="{
      'app-snap-scroll-x': props.direction === 'X',
      'app-snap-scroll-y': props.direction === 'Y',
      'snap-enabled': isTouchableDevice(),
    }"
  >
    <slot />
    <div ref="contentRef" class="app-snap-scroll-content">
      <slot name="scrollable" />
      <div v-if="props.scrollSpacer" class="scroll-spacer" />
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '../../styles/media' as *;
@use '../../styles/mixins' as *;

.app-snap-scroll {
  overflow: hidden;
  .app-snap-scroll-content {
    @include for-more-or-equal-than-900-px {
      gap: 0 var(--spacing-16);
    }
  }

  &.app-snap-scroll-y {
    > .app-snap-scroll-content {
      width: 100%;
      height: 100%;
      overflow-y: auto;
      overflow-x: hidden;
      display: flex;
      flex-direction: column;

      @include for-less-or-equal-than-1200-px {
        @include no-scroll();
      }
      @include for-more-or-equal-than-1200-px {
        padding-right: var(--spacing-4);
      }
    }
    .scroll-spacer {
      height: 100vh;
      flex-shrink: 0;
    }
    :deep(.app-snap-scroll-content) > *:not(.scroll-spacer) {
      flex-shrink: 0;
    }

    &.snap-enabled {
      > .app-snap-scroll-content {
        -webkit-scroll-snap-type: y mandatory;
        -ms-scroll-snap-type: y mandatory;
        scroll-snap-type: y mandatory;
      }
      :deep(.app-snap-scroll-content) > *:not(.scroll-spacer) {
        -webkit-scroll-snap-align: start;
        scroll-snap-align: start;
      }
    }
  }

  &.app-snap-scroll-x {
    > .app-snap-scroll-content {
      overflow-x: auto;
      overflow-y: hidden;
      display: flex;
      height: 100%;
      justify-content: flex-start;

      @include for-less-or-equal-than-1200-px {
        @include no-scroll();
      }
    }

    :deep(.app-snap-scroll-content) > *:not(.scroll-spacer) {
      flex-shrink: 0;
    }

    &.snap-enabled {
      > .app-snap-scroll-content {
        -webkit-scroll-snap-type: x mandatory;
        -ms-scroll-snap-type: x mandatory;
        scroll-snap-type: x mandatory;
      }
      :deep(.app-snap-scroll-content) > *:not(.scroll-spacer) {
        -webkit-scroll-snap-align: start;
        scroll-snap-align: start;
      }
    }
  }
}
</style>
